import { Box, ModBox, Text } from '@jfs/ui-new'
import { useRef, useState } from 'react'
import { Dimensions, ScrollView } from 'react-native'
import {
  AutoSizer,
  Column,
  OnScrollParams,
  RowMouseEventHandlerParams,
  ScrollEventData,
  ScrollSync,
  SortDirectionType,
  Table,
  TableCellProps,
} from 'react-virtualized'
import 'react-virtualized/styles.css'
import { CustomCellRenderer } from './CustomCellRenderer'
import { CustomHeaderRenderer } from './CustomHeaderRenderer'
import { ActionType } from './NewTableItem/types'
import { freezedColumnStyle, styles } from './styles'
import './styles.css'

interface NewTableProps {
  data: any[]
  headerHeight: number
  rowHeight: number
  height?: number | String
  onRowClick: (info: any) => void
  onCheckChange?: (isChecked: boolean, data: any, index: number, rowData: any) => void
  customWidthFunc?: any
  rowsChecked?: any[]
  tableWidth?: number
  overscanRowCount?: number
  sortBy?: string
  sortDirection?: SortDirectionType
  forceUpdate?: boolean
  handleColumnSearch?: (columnName: string, searchText: string) => void
  isColumnSearchEnabled?: boolean
  headerRowKeys: any
  freezeColumn?: string
  from?: string
  transactionType?: number
  isFreeze?: boolean
  sortDetails?: { key: string; order: string }
  onAction?: (actionType: ActionType, id: string, rowData?: any) => void
  onDownloadFileAction?: (filetype: string, id: string) => void
  onChangeInput?: (rrn: string, value: string) => void
  onInvoiceChange?: (val: string) => void
  hide?: boolean
  onDownloadReportFile?: (data: any) => void
  onDeleteReportFile?: (data: any) => void
  freezeColumnWidth?: number
  getIsAllSelected?: (isChecked: boolean) => void
  isStatusClickable?: boolean
  onStatusPress?: (info: any) => void
  onRowEditClicked?: (isChecked: boolean, index: number, rowData: any) => void
  onRetry?: (data: any) => void
  showHeaderCheckBox?: boolean
  enableCheckBox?: boolean
  commentsData?: Array<{ key: string; heading: string }>
  showDefaultExpandedCommentView?: boolean
}

const NewTable = ({
  data,
  onCheckChange,
  customWidthFunc,
  onRowClick,
  rowsChecked,
  tableWidth = Dimensions.get('window').width,
  headerHeight,
  rowHeight,
  overscanRowCount = 100,
  sortBy,
  sortDirection,
  isColumnSearchEnabled,
  handleColumnSearch,
  headerRowKeys,
  freezeColumn,
  from,
  height: tableHeight,
  isFreeze,
  onAction,
  onDownloadFileAction,
  onChangeInput,
  onInvoiceChange,
  hide,
  onDownloadReportFile,
  onDeleteReportFile,
  freezeColumnWidth = 150,
  getIsAllSelected,
  isStatusClickable = false,
  onStatusPress,
  onRowEditClicked,
  onRetry,
  showHeaderCheckBox,
  enableCheckBox,
  commentsData,
  showDefaultExpandedCommentView,
}: NewTableProps) => {
  const tableRef = useRef(null)

  const downloadFileAction = (filetype: string, rowIndex: number) => {
    if (onDownloadFileAction) {
      onDownloadFileAction(filetype, data[rowIndex]?.id)
    }
  }

  const deleteReportFile = (itemData: any) => {
    if (onDeleteReportFile) {
      onDeleteReportFile(itemData)
    }
  }

  const downloadReportFile = (itemData: any) => {
    if (onDownloadReportFile) {
      onDownloadReportFile(itemData)
    }
  }

  const sortList = ({ sortBy, sortDirection }: any) => {
    console.log('sortDirection', sortDirection)
    if (sortBy && sortDirection) {
      let list = data
      if (data.length > 0) {
        if (isNaN(data[0][sortBy])) {
          list = list.sort((a, b) => {
            return a[sortBy].localeCompare(b[sortBy])
          })
          return sortDirection === 'DESC' ? list.reverse() : list
        } else {
          list = list.sort((a, b) => {
            return +a[sortBy] - +b[sortBy]
          })
          return sortDirection === 'DESC' ? list.reverse() : list
        }
      }
    }
    return data
  }

  const sortedList = sortList({ sortBy, sortDirection })

  const [sortState, setSortState] = useState({ sortBy, sortDirection, sortedList })

  if (hide) {
    return null
  }

  const sort = ({ sortBy, sortDirection }: any) => {
    const sortedList = sortList({ sortBy, sortDirection })
    setSortState({ sortBy, sortDirection, sortedList })
  }

  const rowGetter = (index: number) => {
    return sortedList[index]
  }

  const headerRenderer = ({ columnData, dataKey, disableSort, label, sortBy, sortDirection }: any) => {
    return (
      <CustomHeaderRenderer
        columnData={columnData}
        dataKey={dataKey}
        disableSort={disableSort}
        label={label}
        sortBy={sortBy}
        sortDirection={sortDirection}
        styles={styles}
        isColumnSearchEnabled={isColumnSearchEnabled}
        onSearchTextChange={handleColumnSearch}
        headerRowKeys={headerRowKeys}
        getIsAllSelected={getIsAllSelected}
        showHeaderCheckBox={showHeaderCheckBox}
      />
    )
  }

  const cellRenderer = ({ cellData, columnData, dataKey, rowData, rowIndex }: TableCellProps) => {
    return (
      <CustomCellRenderer
        cellData={cellData}
        columnData={columnData}
        dataKey={dataKey}
        rowData={rowData}
        rowIndex={rowIndex}
        styles={styles}
        onCheckChange={onCheckChange}
        isRowChecked={rowsChecked?.includes(rowIndex) ?? false}
        tableRef={tableRef}
        from={from}
        data={data}
        isFreeze={isFreeze}
        freezeColumn={freezeColumn}
        onAction={onAction}
        onDownloadFileAction={downloadFileAction}
        onChangeInput={onChangeInput}
        onInvoiceChange={onInvoiceChange}
        onDownloadReportFile={downloadReportFile}
        onDeleteReportFile={deleteReportFile}
        onRowClick={onRowClick}
        isStatusClickable={isStatusClickable}
        onStatusPress={onStatusPress}
        onRowEditClicked={onRowEditClicked}
        onRetry={onRetry}
        enableCheckBox={enableCheckBox}
        commentsData={commentsData}
        showDefaultExpandedCommentView={showDefaultExpandedCommentView}
      />
    )
  }

  const noRowsRenderer = () => {
    return (
      <Box margin={16}>
        <Text type={'subheading'} color={'BLACK'} title={'No records found'} />
      </Box>
    )
  }

  const isFixedColumnValid = (columnName: string) => {
    return Object.keys(headerRowKeys).includes(columnName)
  }

  const updateHoverStyles = (id: string, index: number, isHover: boolean) => {
    if (freezeColumn && isFixedColumnValid(freezeColumn)) {
      const table = document.getElementById(id)
      const rows = table && table.getElementsByClassName('ReactVirtualized__Table__row')
      if (rows && rows.length > 0 && rows[index]) {
        rows[index].style.backgroundColor = isHover ? '#F8F8F8' : '#FFFFFF'
      }
    }
  }

  const onTableHover = (info: RowMouseEventHandlerParams) => {
    updateHoverStyles('nonFreezeTable', info.index, true)
    updateHoverStyles('freezedTable', info.index, true)
  }

  const onTableRowMouseOut = (info: RowMouseEventHandlerParams) => {
    updateHoverStyles('nonFreezeTable', info.index, false)
    updateHoverStyles('freezedTable', info.index, false)
  }

  // const checkSelectionOnPress = (info: RowMouseEventHandlerParams) => {
  //   const selectedData = window && window.getSelection()
  //   if (!(selectedData && selectedData.toString())) {
  //     onRowClick && onRowClick(info)
  //   }
  // }
  const cellDataGetter = ({ rowData, dataKey }) => rowData?.[dataKey]
  const getColumnWidth = (header) =>
    customWidthFunc ? customWidthFunc(header) : tableWidth / Object.keys(headerRowKeys)?.length

  return (
    <ScrollSync>
      {({ onScroll, scrollTop }) => {
        const onTableScroll = (info: ScrollEventData) => {
          onScroll({
            clientHeight: info.clientHeight,
            scrollHeight: info.scrollHeight,
            scrollTop: info.scrollTop,
          } as OnScrollParams)
        }
        return (
          <ModBox {...styles.rootContainer(tableHeight)}>
            {freezeColumn && isFixedColumnValid(freezeColumn) && (
              <ModBox {...freezedColumnStyle(freezeColumnWidth)}>
                <AutoSizer>
                  {({ height, width }) => (
                    <Table
                      id={'freezedTable'}
                      headerHeight={headerHeight}
                      height={height}
                      rowCount={data?.length}
                      rowGetter={({ index }) => rowGetter(index)}
                      rowHeight={rowHeight}
                      width={width}
                      overscanRowCount={overscanRowCount}
                      scrollTop={scrollTop}
                      sort={sort}
                      onScroll={onTableScroll}
                      sortBy={sortState.sortBy}
                      sortDirection={sortState.sortDirection}
                      onRowMouseOver={onTableHover}
                      onRowMouseOut={onTableRowMouseOut}>
                      <Column
                        disableSort={freezeColumn !== sortState.sortBy}
                        label={freezeColumn}
                        width={width}
                        dataKey={freezeColumn}
                        headerRenderer={headerRenderer}
                        cellRenderer={cellRenderer}
                        cellDataGetter={cellDataGetter}
                        flexGrow={1}
                        flexShrink={1}
                      />
                    </Table>
                  )}
                </AutoSizer>
              </ModBox>
            )}
            <ScrollView horizontal nestedScrollEnabled>
              <AutoSizer>
                {({ height, width }) => {
                  return (
                    <Table
                      id={'nonFreezeTable'}
                      ref={tableRef}
                      headerHeight={headerHeight}
                      height={height}
                      rowCount={data?.length}
                      rowGetter={({ index }) => rowGetter(index)}
                      rowHeight={rowHeight}
                      width={tableWidth}
                      onScroll={onTableScroll}
                      scrollTop={scrollTop}
                      overscanRowCount={overscanRowCount}
                      sort={sort}
                      sortBy={sortState.sortBy}
                      sortDirection={sortState.sortDirection}
                      noRowsRenderer={noRowsRenderer}
                      onRowMouseOver={onTableHover}
                      onRowMouseOut={onTableRowMouseOut}>
                      {headerRowKeys &&
                        Object.keys(headerRowKeys).map((header: any, index: number) => {
                          return header !== freezeColumn ? (
                            <Column
                              disableSort={header !== sortState.sortBy}
                              key={index}
                              width={getColumnWidth(header)}
                              label={header}
                              dataKey={header}
                              headerRenderer={headerRenderer}
                              cellRenderer={cellRenderer}
                              cellDataGetter={cellDataGetter}
                              flexGrow={1}
                              flexShrink={1}
                            />
                          ) : null
                        })}
                    </Table>
                  )
                }}
              </AutoSizer>
            </ScrollView>
          </ModBox>
        )
      }}
    </ScrollSync>
  )
}
export { NewTable }
